import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { showNotification } from '@mantine/notifications';
import {
  validateEvidence,
  dismissEvidence,
  sendEvidenceBackToInbox,
  unassignEvidence,
  updateEvidence,
  ValidateEvidencePayload,
  RejectInfo,
} from '../../../api/evidences';
import { useEvidencesStore } from './evidencesStore';
import type { Evidence } from './types';

interface UseEvidenceActionsReturn {
  assignToIdea: (evidenceId: string, payload: ValidateEvidencePayload) => Promise<void>;
  dismiss: (evidenceId: string, rejectInfo?: RejectInfo) => Promise<void>;
  backToInbox: (evidenceId: string) => Promise<void>;
  unassign: (evidenceId: string, ideaId: string) => Promise<void>;
  updateField: (evidenceId: string, updates: Partial<Evidence>) => Promise<Evidence | undefined>;
  isLoading: {
    assign: boolean;
    dismiss: boolean;
    backToInbox: boolean;
    unassign: boolean;
    update: boolean;
  };
}

const loadingState = {
  assign: false,
  dismiss: false,
  backToInbox: false,
  unassign: false,
  update: false,
};

export const useEvidenceActions = (): UseEvidenceActionsReturn => {
  const queryClient = useQueryClient();
  const auth0 = useAuth0();
  
  const [isLoading, setIsLoading] = React.useState(loadingState);
  
  const setLoading = (key: keyof typeof loadingState, value: boolean) => {
    setIsLoading((prev) => ({ ...prev, [key]: value }));
  };

  const assignToIdea = async (evidenceId: string, payload: ValidateEvidencePayload) => {
    console.log('🎯 assignToIdea called', { evidenceId, payload });
    
    const state = useEvidencesStore.getState();
    // Check both evidences list and openedEvidence (for modal)
    const evidence = state.evidences.find((e) => e._id === evidenceId) || 
                     (state.openedEvidence?._id === evidenceId ? state.openedEvidence : null);
    
    console.log('🔍 Evidence lookup:', {
      foundInList: !!state.evidences.find((e) => e._id === evidenceId),
      foundInModal: state.openedEvidence?._id === evidenceId,
      evidence: evidence ? { _id: evidence._id, title: evidence.title } : null
    });

    // 1. Optimistically remove from list (if it exists there)
    if (state.evidences.find((e) => e._id === evidenceId)) {
      console.log('📝 Removing evidence from list optimistically');
      useEvidencesStore.getState().optimisticRemoveEvidence(evidenceId, 'assign');
    }
    
    // 2. Close modal if this evidence is open
    if (state.openedEvidence?._id === evidenceId) {
      console.log('🚪 Closing modal');
      useEvidencesStore.getState().closeModal();
    }
    
    setLoading('assign', true);

    try {
      // 3. API call - always make the call even if evidence not in store
      console.log('📡 Calling validateEvidence API...', { evidenceId, payload, auth0: !!auth0 });
      const response = await validateEvidence(evidenceId, payload, auth0);
      console.log('✅ API call successful', response);
      
      // 4. Invalidate React Query cache
      queryClient.invalidateQueries({ queryKey: ['evidence-view-count'] });
      
      showNotification({
        title: 'Success',
        message: 'Evidence validated and assigned to idea',
        color: 'teal',
        autoClose: 3000,
      });
    } catch (error: any) {
      console.error('❌ API call failed:', error);
      
      // 5. Rollback on error
      // Only rollback if evidence was in the list (not just in modal)
      const currentState = useEvidencesStore.getState();
      if (evidence && currentState.evidences.find((e) => e._id === evidenceId)) {
        console.log('🔄 Rolling back evidence');
        useEvidencesStore.getState().rollbackEvidence(evidenceId, evidence);
      }
      
      showNotification({
        title: 'Error',
        message: error?.message || 'Failed to validate evidence',
        color: 'red',
        autoClose: 5000,
      });
      throw error;
    } finally {
      setLoading('assign', false);
    }
  };

  const dismiss = async (evidenceId: string, rejectInfo?: RejectInfo) => {
    const state = useEvidencesStore.getState();
    // Check both evidences list and openedEvidence (for modal)
    const evidence = state.evidences.find((e) => e._id === evidenceId) || 
                     (state.openedEvidence?._id === evidenceId ? state.openedEvidence : null);
    
    console.log('🎯 dismiss called', { evidenceId, rejectInfo, foundInList: !!state.evidences.find((e) => e._id === evidenceId), foundInModal: state.openedEvidence?._id === evidenceId });

    // 1. Optimistically remove from list (if it exists there)
    if (state.evidences.find((e) => e._id === evidenceId)) {
      useEvidencesStore.getState().optimisticRemoveEvidence(evidenceId, 'dismiss');
    }
    
    // 2. Close modal if this evidence is open (but don't close the decline modal yet - let it close after success)
    // We'll close it in the DeclineModalContent after the action completes
    
    setLoading('dismiss', true);

    try {
      // 3. API call
      await dismissEvidence(evidenceId, { rejectInfo }, auth0);
      
      // 4. Invalidate React Query cache
      queryClient.invalidateQueries({ queryKey: ['evidence-view-count'] });
      
      showNotification({
        title: 'Success',
        message: 'Evidence declined',
        color: 'orange',
        autoClose: 3000,
      });
    } catch (error: any) {
      // 5. Rollback on error
      useEvidencesStore.getState().rollbackEvidence(evidenceId, evidence);
      
      showNotification({
        title: 'Error',
        message: error?.message || 'Failed to decline evidence',
        color: 'red',
        autoClose: 5000,
      });
      throw error;
    } finally {
      setLoading('dismiss', false);
    }
  };

  const backToInbox = async (evidenceId: string) => {
    const state = useEvidencesStore.getState();
    const evidence = state.evidences.find((e) => e._id === evidenceId);
    
    if (!evidence) {
      console.error('Evidence not found for back to inbox:', evidenceId);
      return;
    }

    // 1. Optimistically remove from list (it will move to inbox view)
    useEvidencesStore.getState().optimisticRemoveEvidence(evidenceId, 'backToInbox');
    
    // 2. Close modal if this evidence is open
    if (state.openedEvidence?._id === evidenceId) {
      useEvidencesStore.getState().closeModal();
    }
    
    setLoading('backToInbox', true);

    try {
      // 3. API call
      await sendEvidenceBackToInbox(evidenceId, auth0);

      // 4. Invalidate React Query cache
      queryClient.invalidateQueries({ queryKey: ['evidence-view-count'] });
      
      showNotification({
        title: 'Success',
        message: 'Evidence sent back to inbox',
        color: 'blue',
        autoClose: 3000,
      });
    } catch (error: any) {
      // 5. Rollback on error
      useEvidencesStore.getState().rollbackEvidence(evidenceId, evidence);
      
      showNotification({
        title: 'Error',
        message: error?.message || 'Failed to send evidence back to inbox',
        color: 'red',
        autoClose: 5000,
      });
      throw error;
    } finally {
      setLoading('backToInbox', false);
    }
  };

  const unassign = async (evidenceId: string, ideaId: string) => {
    const state = useEvidencesStore.getState();
    const evidence = state.evidences.find((e) => e._id === evidenceId) ||
                     (state.openedEvidence?._id === evidenceId ? state.openedEvidence : null);

    if (state.evidences.find((e) => e._id === evidenceId)) {
      useEvidencesStore.getState().optimisticRemoveEvidence(evidenceId, 'unassign');
    }

    if (state.openedEvidence?._id === evidenceId) {
      useEvidencesStore.getState().closeModal();
    }

    setLoading('unassign', true);

    try {
      await unassignEvidence(evidenceId, ideaId, auth0);

      queryClient.invalidateQueries({ queryKey: ['evidence-view-count'] });

      showNotification({
        title: 'Success',
        message: 'Evidence unassigned from idea',
        color: 'blue',
        autoClose: 3000,
      });
    } catch (error: any) {
      if (evidence) {
        useEvidencesStore.getState().rollbackEvidence(evidenceId, evidence);
      }

      showNotification({
        title: 'Error',
        message: error?.message || 'Failed to unassign evidence',
        color: 'red',
        autoClose: 5000,
      });
      throw error;
    } finally {
      setLoading('unassign', false);
    }
  };

  const resolveEvidenceForUpdate = (
    state: ReturnType<typeof useEvidencesStore.getState>,
    evidenceId: string
  ): Evidence | null =>
    state.evidences.find((e) => e._id === evidenceId) ||
    (state.openedEvidence?._id === evidenceId ? state.openedEvidence : null);

  const updateField = async (evidenceId: string, updates: Partial<Evidence>) => {
    const state = useEvidencesStore.getState();
    const evidenceSnapshot = resolveEvidenceForUpdate(state, evidenceId);
    const isInList = state.evidences.some((e) => e._id === evidenceId);

    if (isInList) {
      useEvidencesStore.getState().optimisticUpdateEvidence(evidenceId, updates);
    }
    if (state.openedEvidence?._id === evidenceId) {
      useEvidencesStore.getState().updateOpenedEvidence(updates);
    }

    setLoading('update', true);

    try {
      const response = await updateEvidence(evidenceId, updates, auth0);
      const updatedEvidence = response?.evidence || response;
      
      if (updatedEvidence) {
        if (isInList) {
          useEvidencesStore.getState().optimisticUpdateEvidence(evidenceId, updatedEvidence);
        }
        const currentState = useEvidencesStore.getState();
        if (currentState.openedEvidence?._id === evidenceId) {
          useEvidencesStore.getState().updateOpenedEvidence(updatedEvidence);
        }
      }

      queryClient.invalidateQueries({
        queryKey: ['evidences', 'list'],
        refetchType: 'none', // Don't refetch, just mark stale
      });
      
      showNotification({
        title: 'Success',
        message: 'Evidence updated',
        color: 'teal',
        autoClose: 2000,
      });
      
      // Return the updated evidence so the caller can use it
      return updatedEvidence;
    } catch (error: any) {
      if (evidenceSnapshot) {
        if (isInList) {
          useEvidencesStore.getState().optimisticUpdateEvidence(evidenceId, evidenceSnapshot);
        }
        const currentState = useEvidencesStore.getState();
        if (currentState.openedEvidence?._id === evidenceId) {
          useEvidencesStore.getState().updateOpenedEvidence(evidenceSnapshot);
        }
      }
      
      showNotification({
        title: 'Error',
        message: error?.message || 'Failed to update evidence',
        color: 'red',
        autoClose: 5000,
      });
      throw error;
    } finally {
      setLoading('update', false);
    }
  };

  return {
    assignToIdea,
    dismiss,
    backToInbox,
    unassign,
    updateField,
    isLoading,
  };
};

